/**
 * DayView component for calendar
 * Displays events for a single day in a timeline layout
 */

'use client';

import type { Event } from '../types';
import { Calendar } from 'lucide-react';
import { useMemo } from 'react';
import { EventCard } from '../cards/event-card';

interface DayViewProps {
  events: Event[];
  currentDate: Date;
  baseUrl?: string;
}

export function DayView({ events, currentDate, baseUrl = '/events' }: DayViewProps) {
  // Get events for the current day
  const dayEvents = useMemo(() => {
    const targetDate = new Date(currentDate);
    targetDate.setHours(0, 0, 0, 0);

    const filtered = events.filter(event => {
      const startTimestamp = event.eventDateTimeRange?.start?.timestamp;
      const endTimestamp = event.eventDateTimeRange?.end?.timestamp;
      if (!startTimestamp || !endTimestamp) return false;

      const eventStart = new Date(startTimestamp * 1000);
      eventStart.setHours(0, 0, 0, 0);

      const eventEnd = new Date(endTimestamp * 1000);
      eventEnd.setHours(23, 59, 59, 999);

      return targetDate >= eventStart && targetDate <= eventEnd;
    });

    // Sort by time
    return filtered.sort((a, b) => {
      const aStart = a.eventDateTimeRange?.start?.timestamp || 0;
      const bStart = b.eventDateTimeRange?.start?.timestamp || 0;
      return aStart - bStart;
    });
  }, [currentDate, events]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isToday = currentDate.getTime() === today.getTime();

  return (
    <div className="p-6">
      {/* Day header */}
      <div className={`mb-6 rounded-lg p-4 ${isToday ? 'bg-primary/10' : 'bg-muted/50'}`}>
        <div className="flex items-center gap-3">
          <div
            className={`flex h-16 w-16 flex-col items-center justify-centre rounded-lg ${
              isToday ? 'bg-primary text-primary-foreground' : 'bg-background'
            }`}
          >
            <div className={`text-xs font-medium ${isToday ? '' : 'text-muted-foreground'}`}>
              {currentDate.toLocaleDateString('en-GB', { month: 'short' })}
            </div>
            <div className="text-2xl font-bold">{currentDate.getDate()}</div>
          </div>
          <div>
            <h3 className="text-lg font-semibold">
              {currentDate.toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </h3>
            <p className="text-sm text-muted-foreground">
              {dayEvents.length} {dayEvents.length === 1 ? 'event' : 'events'}
            </p>
          </div>
        </div>
      </div>

      {/* Events grid */}
      {dayEvents.length === 0 ? (
        <div className="flex min-h-[300px] flex-col items-center justify-centre rounded-lg border border-dashed bg-muted/20 p-8 text-centre">
          <Calendar className="mb-4 h-12 w-12 text-muted-foreground/50" />
          <p className="text-lg font-medium text-muted-foreground">No event on this day</p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {dayEvents.map(event => {
            if (!event.path) return null;

            return (
              <li key={event.id} className="w-full min-w-0">
                <EventCard
                  event={event}
                  baseUrl={baseUrl}
                  variant="default"
                  clickable={false}
                  showPastIndicator={true}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
